import { create } from 'zustand';
import { endpoints } from '../config/api';

export interface RackRow {
  id: string;
  zone_id: string;
  aisle_number: number;
  row_side: 'left' | 'right' | 'both';
  num_bays: number;
  num_levels: number;
  bay_width_in: number;
  bay_depth_in: number;
  level_height_in: number;
  total_length_in?: number;
  total_height_in?: number;
  location_prefix?: string;
  level_naming: 'alpha' | 'numeric';
  has_left_side: boolean;
  has_right_side: boolean;
  location_count: number;
  // Local coordinates for canvas
  local_start_x?: number;
  local_start_y?: number;
  local_end_x?: number;
  local_end_y?: number;
  // Geo coordinates
  geo_start_lat?: number;
  geo_start_lon?: number;
  geo_end_lat?: number;
  geo_end_lon?: number;
}

export interface RackRowDraft {
  aisle_number: number;
  row_side: 'left' | 'right' | 'both';
  num_bays: number;
  num_levels: number;
  bay_width_in: number;
  bay_depth_in: number;
  level_height_in: number;
  location_prefix: string;
  level_naming: 'alpha' | 'numeric';
  has_left_side: boolean;
  has_right_side: boolean;
  local_start_x?: number;
  local_start_y?: number;
  local_end_x?: number;
  local_end_y?: number;
}

export interface Zone {
  id: string;
  building_id: string;
  name: string;
  code: string;
  zone_type: string;
  floor_level: number;
}

interface DrawingState {
  isDrawing: boolean;
  startPoint: { x: number; y: number } | null;
  endPoint: { x: number; y: number } | null;
}

interface RackEditorState {
  // Data
  zones: Zone[];
  selectedZone: Zone | null;
  rackRows: RackRow[];
  selectedRackRow: RackRow | null;

  // UI State
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;

  // Drawing state
  drawing: DrawingState;
  currentDraft: RackRowDraft;

  // Background image
  backgroundImage: string | null;
  imageScale: number;
  imageOffsetX: number;
  imageOffsetY: number;

  // Actions
  openEditor: (buildingId: string) => Promise<void>;
  closeEditor: () => void;
  selectZone: (zone: Zone) => Promise<void>;
  selectRackRow: (rackRow: RackRow | null) => void;

  // Rack row CRUD
  createRackRow: () => Promise<void>;
  updateRackRow: (id: string, updates: Partial<RackRow>) => Promise<void>;
  deleteRackRow: (id: string) => Promise<void>;
  generateLocations: (id: string) => Promise<{ locations_created: number }>;

  // Drawing
  startDrawing: (x: number, y: number) => void;
  updateDrawing: (x: number, y: number) => void;
  finishDrawing: () => void;
  cancelDrawing: () => void;

  // Draft
  updateDraft: (updates: Partial<RackRowDraft>) => void;
  resetDraft: () => void;

  // Background
  setBackgroundImage: (url: string | null) => void;
  setImageTransform: (scale: number, offsetX: number, offsetY: number) => void;

  // Export
  exportLocations: (format: 'csv' | 'json') => Promise<void>;
}

const defaultDraft: RackRowDraft = {
  aisle_number: 1,
  row_side: 'both',
  num_bays: 10,
  num_levels: 4,
  bay_width_in: 96,
  bay_depth_in: 48,
  level_height_in: 72,
  location_prefix: '',
  level_naming: 'alpha',
  has_left_side: true,
  has_right_side: true,
};

export const useRackEditorStore = create<RackEditorState>((set, get) => ({
  // Initial state
  zones: [],
  selectedZone: null,
  rackRows: [],
  selectedRackRow: null,
  isOpen: false,
  isLoading: false,
  error: null,
  drawing: {
    isDrawing: false,
    startPoint: null,
    endPoint: null,
  },
  currentDraft: { ...defaultDraft },
  backgroundImage: null,
  imageScale: 1,
  imageOffsetX: 0,
  imageOffsetY: 0,

  // Actions
  openEditor: async (buildingId: string) => {
    set({ isOpen: true, isLoading: true, error: null });
    try {
      const response = await fetch(endpoints.zones(buildingId), { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch zones');
      const zones: Zone[] = await response.json();
      set({ zones, isLoading: false });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  closeEditor: () => {
    set({
      isOpen: false,
      zones: [],
      selectedZone: null,
      rackRows: [],
      selectedRackRow: null,
      drawing: { isDrawing: false, startPoint: null, endPoint: null },
      currentDraft: { ...defaultDraft },
    });
  },

  selectZone: async (zone: Zone) => {
    set({ selectedZone: zone, isLoading: true, error: null, rackRows: [], selectedRackRow: null });
    try {
      const response = await fetch(endpoints.rackRows(zone.id), { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch rack rows');
      const rackRows: RackRow[] = await response.json();
      // Update draft prefix to zone code
      set({
        rackRows,
        isLoading: false,
        currentDraft: { ...get().currentDraft, location_prefix: zone.code },
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  selectRackRow: (rackRow: RackRow | null) => {
    set({ selectedRackRow: rackRow });
  },

  createRackRow: async () => {
    const { selectedZone, currentDraft, drawing } = get();
    if (!selectedZone) return;

    set({ isLoading: true, error: null });
    try {
      const body = {
        ...currentDraft,
        local_start_x: drawing.startPoint?.x,
        local_start_y: drawing.startPoint?.y,
        local_end_x: drawing.endPoint?.x,
        local_end_y: drawing.endPoint?.y,
      };

      const response = await fetch(endpoints.rackRows(selectedZone.id), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create rack row');
      }

      const newRackRow: RackRow = await response.json();
      set((state) => ({
        rackRows: [...state.rackRows, newRackRow],
        selectedRackRow: newRackRow,
        isLoading: false,
        drawing: { isDrawing: false, startPoint: null, endPoint: null },
        currentDraft: {
          ...defaultDraft,
          location_prefix: selectedZone.code,
          aisle_number: currentDraft.aisle_number + 1,
        },
      }));
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  updateRackRow: async (id: string, updates: Partial<RackRow>) => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(endpoints.rackRowById(id), {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update rack row');
      }

      const updatedRackRow: RackRow = await response.json();
      set((state) => ({
        rackRows: state.rackRows.map((rr) => (rr.id === id ? updatedRackRow : rr)),
        selectedRackRow: state.selectedRackRow?.id === id ? updatedRackRow : state.selectedRackRow,
        isLoading: false,
      }));
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  deleteRackRow: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(endpoints.rackRowById(id), {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete rack row');
      }

      set((state) => ({
        rackRows: state.rackRows.filter((rr) => rr.id !== id),
        selectedRackRow: state.selectedRackRow?.id === id ? null : state.selectedRackRow,
        isLoading: false,
      }));
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  generateLocations: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(endpoints.generateLocations(id), {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate locations');
      }

      const result = await response.json();

      // Refresh rack rows to get updated location count
      const { selectedZone } = get();
      if (selectedZone) {
        const rowsResponse = await fetch(endpoints.rackRows(selectedZone.id), { credentials: 'include' });
        if (rowsResponse.ok) {
          const rackRows: RackRow[] = await rowsResponse.json();
          set({ rackRows, isLoading: false });
        }
      } else {
        set({ isLoading: false });
      }

      return result;
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      throw error;
    }
  },

  // Drawing
  startDrawing: (x: number, y: number) => {
    set({
      drawing: {
        isDrawing: true,
        startPoint: { x, y },
        endPoint: { x, y },
      },
    });
  },

  updateDrawing: (x: number, y: number) => {
    const { drawing } = get();
    if (!drawing.isDrawing) return;
    set({
      drawing: {
        ...drawing,
        endPoint: { x, y },
      },
    });
  },

  finishDrawing: () => {
    const { drawing } = get();
    if (!drawing.isDrawing || !drawing.startPoint || !drawing.endPoint) return;
    set({
      drawing: {
        ...drawing,
        isDrawing: false,
      },
    });
  },

  cancelDrawing: () => {
    set({
      drawing: {
        isDrawing: false,
        startPoint: null,
        endPoint: null,
      },
    });
  },

  // Draft
  updateDraft: (updates: Partial<RackRowDraft>) => {
    set((state) => ({
      currentDraft: { ...state.currentDraft, ...updates },
    }));
  },

  resetDraft: () => {
    const { selectedZone } = get();
    set({
      currentDraft: {
        ...defaultDraft,
        location_prefix: selectedZone?.code || '',
      },
      drawing: { isDrawing: false, startPoint: null, endPoint: null },
    });
  },

  // Background
  setBackgroundImage: (url: string | null) => {
    set({ backgroundImage: url });
  },

  setImageTransform: (scale: number, offsetX: number, offsetY: number) => {
    set({ imageScale: scale, imageOffsetX: offsetX, imageOffsetY: offsetY });
  },

  // Export
  exportLocations: async (format: 'csv' | 'json') => {
    const { selectedZone } = get();
    if (!selectedZone) return;

    set({ isLoading: true, error: null });
    try {
      const response = await fetch(endpoints.exportLocations(selectedZone.id, format), {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to export locations');
      }

      if (format === 'csv') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `locations_${selectedZone.code}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `locations_${selectedZone.code}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }

      set({ isLoading: false });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },
}));
